import { useState, useCallback, useRef } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useFormValidation, type FormData } from '../../hooks/useFormValidation';
import { useErrorHandling } from '../../hooks/useErrorHandling';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { useToast } from '../Toast';
import { LoadingSpinner } from '../Loading';
import { NetworkErrorMessage } from '../ErrorMessage';
import { emailService } from '../../services/emailService.js';
import { supabase } from '../../services/supabaseClient.js';
import { ReferralWidget } from '../ReferralWidget';
import Captcha, { type CaptchaRef } from './Captcha';
// @ts-ignore - JS module without types
import { ERROR_MESSAGES, SUCCESS_MESSAGES, USER_ERROR_MESSAGES } from '../../utils/constants.js';

interface SubmissionError {
  type: 'DUPLICATE_EMAIL' | 'DUPLICATE_WALLET' | 'DATABASE_ERROR' | 'EMAIL_SEND_ERROR' | 'NETWORK_ERROR' | 'VALIDATION_ERROR' | 'UNKNOWN_ERROR' | 'CAPTCHA_ERROR';
  message: string;
  field?: keyof FormData;
}

interface WhitelistFormProps {
  onSubmitSuccess?: (data: FormData & { id: string }) => void;
  onSubmitError?: (error: SubmissionError) => void;
  className?: string;
}

export function WhitelistForm({
  onSubmitSuccess,
  onSubmitError,
  className = ''
}: WhitelistFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<SubmissionError | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successData, setSuccessData] = useState<(FormData & { id: string }) | null>(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const captchaRef = useRef<CaptchaRef>(null);

  const { normalizeFormData, getFieldValidation } = useFormValidation();
  const { handleError } = useErrorHandling();
  const { isOnline, isSlowConnection } = useNetworkStatus();
  const { showSuccess, showError, showWarning } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      walletAddress: ''
    }
  });

  const handleFormSubmit: SubmitHandler<FormData> = useCallback(async (data) => {
    if (!isOnline) {
      showError(USER_ERROR_MESSAGES.NETWORK_OFFLINE.title, USER_ERROR_MESSAGES.NETWORK_OFFLINE.message);
      return;
    }

    if (isSlowConnection()) {
      showWarning(USER_ERROR_MESSAGES.NETWORK_SLOW.title, USER_ERROR_MESSAGES.NETWORK_SLOW.message, 5000);
    }

    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const captchaData = captchaRef.current?.getCaptchaData();
      if (!captchaData || !captchaData.token) {
        throw new Error('CAPTCHA token not available.');
      }

      const { data: result, error } = await supabase.functions.invoke('submit-whitelist', {
        body: {
          formData: normalizeFormData(data),
          captchaAnswer: captchaData.answer,
          captchaToken: captchaData.token,
        },
      });

      if (error) throw error;

      if (result.success) {
        const successEntry = { ...normalizeFormData(data), id: result.data.id };
        setSuccessData(successEntry);
        setIsSuccess(true);
        reset();
        captchaRef.current?.regenerate();
        showSuccess('Registration Successful! 🎉', 'Welcome to the $PGC whitelist! Check your email for confirmation.', 8000);
        if (onSubmitSuccess) onSubmitSuccess(successEntry);
        emailService.sendAllNotifications(successEntry);
      } else {
        const errorPayload: SubmissionError = {
          type: result.error || 'UNKNOWN_ERROR',
          message: result.message || ERROR_MESSAGES.UNKNOWN_ERROR,
        };
        setSubmissionError(errorPayload);
        captchaRef.current?.regenerate();
        showError(USER_ERROR_MESSAGES.SUBMISSION_FAILED.title, errorPayload.message, 8000);
        if (onSubmitError) onSubmitError(errorPayload);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      const submissionError: SubmissionError = {
        type: 'UNKNOWN_ERROR',
        message: (err as Error).message || ERROR_MESSAGES.UNKNOWN_ERROR,
      };
      setSubmissionError(submissionError);
      captchaRef.current?.regenerate();
      await handleError(err, 'form-submission', { showToast: true, retryable: false });
      if (onSubmitError) onSubmitError(submissionError);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    normalizeFormData, 
    onSubmitSuccess, 
    onSubmitError, 
    reset, 
    isOnline, 
    isSlowConnection, 
    showSuccess, 
    showError, 
    showWarning, 
    handleError
  ]);

  const handleRetrySubmission = useCallback(() => {
    setSubmissionError(null);
    setIsSuccess(false);
  }, []);

  const handleNewRegistration = useCallback(() => {
    setIsSuccess(false);
    setSuccessData(null);
    setSubmissionError(null);
    reset();
  }, [reset]);

  if (isSuccess && successData) {
    return <SuccessMessage data={successData} onNewRegistration={handleNewRegistration} className={className} />;
  }

  return (
    <div className={`max-w-md mx-auto bg-[#1E2329] p-8 rounded-lg border border-[#2B3139] shadow-2xl ${className}`}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Join the Whitelist</h2>
          <p className="text-[#848E9C] text-base">Enter your details to secure your spot in the $PGC airdrop</p>
        </div>

        {!isOnline && <NetworkErrorMessage onRetry={handleRetrySubmission} className="mb-6" />}

        {submissionError && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
              <div className="flex-1">
                <h3 className="text-red-800 font-semibold text-sm mb-1">Registration Failed</h3>
                <p className="text-red-700 text-sm">{submissionError.message}</p>
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <label htmlFor="name" className="block text-base font-semibold mb-2 text-white">Full Name <span className="text-[#F84960]">*</span></label>
          <input id="name" type="text" placeholder="Enter your full name" disabled={isSubmitting} className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-300 transform hover:scale-[1.02] focus:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-opacity-50 placeholder-gray-400 text-lg font-medium ${errors.name ? 'border-[#F84960] bg-[#2B3139] text-white focus:border-[#F84960] focus:ring-[#F84960]' : 'border-[#2B3139] bg-[#2B3139] text-white hover:border-[#848E9C] focus:border-[#F0B90B] focus:ring-[#F0B90B]'}`} {...register('name', getFieldValidation('name'))} />
          {errors.name && <div className="mt-2 flex items-center"><svg className="h-4 w-4 text-[#F84960] mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg><p className="text-sm text-[#F84960] font-medium">{errors.name.message}</p></div>}
        </div>

        <div className="relative">
          <label htmlFor="email" className="block text-base font-semibold mb-2 text-white">Email Address <span className="text-[#F84960]">*</span></label>
          <input id="email" type="email" placeholder="Enter your email address" disabled={isSubmitting} className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-300 transform hover:scale-[1.02] focus:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-opacity-50 placeholder-gray-400 text-lg font-medium ${errors.email ? 'border-[#F84960] bg-[#2B3139] text-white focus:border-[#F84960] focus:ring-[#F84960]' : 'border-[#2B3139] bg-[#2B3139] text-white hover:border-[#848E9C] focus:border-[#F0B90B] focus:ring-[#F0B90B]'}`} {...register('email', getFieldValidation('email'))} />
          {errors.email && <div className="mt-2 flex items-center"><svg className="h-4 w-4 text-[#F84960] mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg><p className="text-sm text-[#F84960] font-medium">{errors.email.message}</p></div>}
        </div>

        <div className="relative">
          <label htmlFor="walletAddress" className="block text-base font-semibold mb-2 text-white">BNB Wallet Address <span className="text-[#F84960]">*</span></label>
          <input id="walletAddress" type="text" placeholder="0x..." disabled={isSubmitting} className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-300 transform hover:scale-[1.02] focus:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-opacity-50 placeholder-gray-400 text-lg font-mono font-medium ${errors.walletAddress ? 'border-[#F84960] bg-[#2B3139] text-white focus:border-[#F84960] focus:ring-[#F84960]' : 'border-[#2B3139] bg-[#2B3139] text-white hover:border-[#848E9C] focus:border-[#F0B90B] focus:ring-[#F0B90B]'}`} {...register('walletAddress', getFieldValidation('walletAddress'))} />
          {errors.walletAddress && <div className="mt-2 flex items-center"><svg className="h-4 w-4 text-[#F84960] mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg><p className="text-sm text-[#F84960] font-medium">{errors.walletAddress.message}</p></div>}
          <div className="mt-2"><p className="text-sm text-[#848E9C]">Enter your Binance Smart Chain (BNB) wallet address. Must start with "0x" followed by 40 hexadecimal characters.</p></div>
        </div>

        <Captcha ref={captchaRef} onUserInput={setCaptchaInput} />

        <button type="submit" disabled={isSubmitting || !isValid || !isDirty || !isOnline || captchaInput.length === 0} className={`w-full py-4 px-6 rounded-lg font-bold text-lg transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-opacity-50 ${isSubmitting || !isValid || !isDirty || !isOnline || captchaInput.length === 0 ? 'bg-[#2B3139] text-[#848E9C] cursor-not-allowed' : 'bg-[#F0B90B] hover:bg-yellow-400 text-black hover:shadow-lg transform hover:scale-105 focus:ring-[#F0B90B]'}`}>
          {isSubmitting ? <div className="flex items-center justify-center"><LoadingSpinner size="medium" color="black" /><span className="ml-3">Registering...</span></div> : !isOnline ? <div className="flex items-center justify-center"><svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" /></svg>Offline</div> : 'Join Whitelist'}
        </button>

        <div className="text-center text-sm">
          {!isOnline && <p className="text-[#F84960] flex items-center justify-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" /></svg>You're offline - form submission disabled</p>}
          {isOnline && !isDirty && <p className="text-[#848E9C]">Fill out the form to join the whitelist</p>}
          {isOnline && isDirty && !isValid && <p className="text-[#F84960]">Please correct the errors above</p>}
          {isOnline && isDirty && isValid && !isSubmitting && <p className="text-[#02C076] flex items-center justify-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Ready to submit!</p>}
          {isSlowConnection() && isOnline && <p className="text-yellow-300 text-xs mt-1">Slow connection detected - submission may take longer</p>}
        </div>

        <div className="text-xs text-[#848E9C] text-center mt-4 p-3 bg-[#2B3139] bg-opacity-50 rounded-lg border border-[#848E9C]">
          <p>By submitting this form, you agree to receive updates about the $PGC airdrop. Your information will be kept secure and will not be shared with third parties.</p>
        </div>
      </form>
    </div>
  );
}

// Success Message Component
interface SuccessMessageProps {
  data: FormData & { id: string };
  onNewRegistration: () => void;
  className?: string;
}

function SuccessMessage({ data, onNewRegistration, className = '' }: SuccessMessageProps) {
  return (
    <div className={`max-w-md mx-auto text-center ${className}`}>
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 shadow-lg">
        <div className="mx-auto w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mb-6"><svg className="w-8 h-8 text-gray-900" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg></div>
        <h2 className="text-2xl font-bold text-white mb-4">Welcome to the Whitelist! 🎉</h2>
        <p className="text-gray-300 mb-6">{SUCCESS_MESSAGES.REGISTRATION_SUCCESS}</p>
        <div className="bg-gray-900 rounded-lg p-4 mb-6 text-left border border-gray-700">
          <h3 className="font-semibold text-yellow-500 mb-3">Registration Details:</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-400">Name:</span><span className="font-medium text-white">{data.name}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Email:</span><span className="font-medium text-white">{data.email}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Wallet:</span><span className="font-mono text-xs text-yellow-500">{`${data.walletAddress.slice(0, 6)}...${data.walletAddress.slice(-4)}`}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Registration ID:</span><span className="font-mono text-xs text-yellow-500">{`${data.id.slice(0, 8)}...`}</span></div>
          </div>
        </div>
        <ReferralWidget userId={data.id} userEmail={data.email} />
        <div className="bg-blue-900 bg-opacity-30 rounded-lg p-4 mb-6 border border-blue-500 border-opacity-50">
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3"><svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>
            <div>
              <h3 className="font-semibold text-blue-500 mb-1">Email Status</h3>
              <p className="text-sm text-blue-300">We're processing your registration information. A confirmation email with your details will be sent to <strong>{data.email}</strong>.</p>
              <p className="text-xs text-blue-400 mt-2">If you don't receive the email within 5 minutes, please check your spam folder.</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 mb-6 border border-yellow-500 border-opacity-30">
          <h3 className="font-semibold text-yellow-500 mb-2">What's Next?</h3>
          <ul className="text-sm text-gray-300 text-left space-y-1">
            <li>• Check your email for confirmation details</li>
            <li>• Follow our social media for airdrop updates</li>
            <li>• Keep your wallet ready for the token distribution</li>
          </ul>
        </div>
        <div className="space-y-3">
          <button onClick={onNewRegistration} className="w-full py-3 px-6 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-colors duration-200">Register Another Address</button>
          <p className="text-xs text-gray-500">Want to register a different wallet address? Click above to start over.</p>
        </div>
      </div>
    </div>
  );
}